"use client"

import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// ─────────────────────────────────────────────────────────────────────────
// Unified button system (PANDA design-system consolidation).
//
// This is the single source of truth for button styling — every button in the
// app should be a <Button> with one of these semantic variants, not a one-off
// `<button className="bg-...">`. Colors come from semantic tokens so the whole
// system retunes from one place.
//
//   default      Primary CTA      — the brand orange→red gradient
//                                    (.bg-panda-gradient). Still the primary
//                                    on marketing/brand surfaces, but NOT on
//                                    modals: see `success`.
//   success      Positive CTA     — the primary action on any modal whose
//                                    action is non-destructive (upgrade,
//                                    trial, top-up, activate, retry, verify,
//                                    restore, save). Green gradient
//                                    (.bg-panda-gradient-success). Since
//                                    PANDA-1779 this is the default choice
//                                    inside a modal; the policy and the
//                                    matching accent classes for hand-rolled
//                                    buttons live in
//                                    `components/ui/modal-accents.ts`.
//   secondary    Secondary        — supporting actions (Cancel, Back, toggles).
//                                    Neutral grey fill.
//   destructive  Destructive      — irreversible actions (Delete project). Solid red.
//   ghost        Ghost / text     — low-emphasis actions (icon buttons, dismiss).
//   outline      Outline          — bordered low-emphasis (kept for compatibility).
//   link         Link             — inline text actions.
// ─────────────────────────────────────────────────────────────────────────
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-full border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Primary CTA — brand orange→red gradient (matches the existing
        // `.bg-panda-gradient` settings/billing CTAs so it's consistent app-wide).
        default:
          "bg-panda-gradient text-white shadow-sm hover:brightness-105 hover:shadow-md",
        // Positive CTA — green gradient for success/momentum framing. Same
        // shape as `default` (gradient + brightness lift) so the two read as
        // one system; only the hue and the focus ring differ.
        // The ring is solid emerald-600, not a translucent tint: the house
        // `ring-ring/50` is 1.5:1 on a white card and 1.3:1 on the dark one,
        // and emerald-500 tops out at 2.6:1 on white at any opacity — all
        // under the 3:1 WCAG 2.2 non-text minimum for a focus indicator.
        // Solid emerald-600 clears it in both themes (3.7:1 / 5.0:1).
        success:
          "bg-panda-gradient-success text-white shadow-sm hover:brightness-110 hover:shadow-md focus-visible:border-emerald-600 focus-visible:ring-emerald-600",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        // Secondary — neutral grey fill for supporting actions.
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        // Destructive — solid red for irreversible actions.
        destructive:
          "bg-destructive text-white shadow-sm hover:bg-destructive/90 focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-full px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-full px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-full in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-full in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
